"use client";

import { useMediaQuery } from "@trydig/ui/hooks";
import type React from "react";
import { createContext, use, useLayoutEffect, useState } from "react";

type Props = {
	children?: React.ReactNode;
};

interface DeviceContext {
	isDesktopXL: boolean;
	isDesktop: boolean;
	isTablet: boolean;
	isMobile: boolean;
	touchEvents: boolean;
}

export const DeviceContext = createContext<DeviceContext>(null!);

const DeviceProvider: React.FC<Props> = ({ children }) => {
	const isDesktopXL = useMediaQuery("(min-width: 1280px)");
	const isDesktop = useMediaQuery("(min-width: 1024px)");
	const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
	const isMobile = useMediaQuery("(max-width: 767px)");
	const [touchEvents, setTouchEvents] = useState(false);

	useLayoutEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		isTouchEnabled() && setTouchEvents(true);
	}, []);

	return (
		<DeviceContext.Provider value={{ isDesktopXL, isDesktop, isTablet, isMobile, touchEvents }}>
			{children}
		</DeviceContext.Provider>
	);
};

export default DeviceProvider;

function isTouchEnabled() {
	return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}

/**
 * ONLY USE THIS HOOK WHEN YOU NEED CONTEXT TO PERFORM SIDE EFFECTS BASED ON DEVICE SIDE
 * @example
 * ```tsx
 * const { mobile } = useDevice()
 * const handleCloseCheckoutModal = () => {
 *    // On mobile we want to close the modal and also redirect back to home
 *    if(mobile) {
 *      closeCheckoutModal()
 *      navigate("/")
 *    }
 *    // On desktop we want to close the modal
 *    else {
 *      closeCheckoutModal()
 *    }
 * }
 * ```
 *
 * @example DONT's
 * ```tsx
 * const {mobile} = useDevice()
 *
 * // ❌ THIS CAUSES A UI FLASH ON DESKTOP DEVICES
 * return (
 *  <div>
 *    {mobile && <div>I'm mobile</div>}
 *    {!mobile && <div>I'm not mobile</div>}
 *  </div>
 * )
 * ```
 */

export const useDevice = () => {
	const { isDesktopXL, isDesktop, isTablet, isMobile, touchEvents } = use(DeviceContext);

	return {
		desktopLarge: isDesktopXL,
		desktop: isDesktop,
		tablet: isTablet,
		mobile: isMobile,
		touchEvents,
	};
};
export type DeviceType = ReturnType<typeof useDevice>;
